import { defineStore } from "pinia"
import { ref, computed, watch } from "vue"

export type ThemePreference = "system" | "light" | "dark"
export type Theme = "light" | "dark"

// Per browser, not per user: company logins are shared, so the device holds the preference.
// Read by the inline script in index.html too, which applies it before the first paint.
const THEME_STORAGE_KEY = "pakketransport.theme"

function loadPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    return stored === "light" || stored === "dark" ? stored : "system"
  } catch {
    return "system"
  }
}

export const useThemeStore = defineStore("theme", () => {
  const preference = ref<ThemePreference>(loadPreference())

  const media = window.matchMedia("(prefers-color-scheme: dark)")
  const systemDark = ref(media.matches)
  // "System" follows the phone live, e.g. when it switches to dark at sunset
  media.addEventListener("change", event => (systemDark.value = event.matches))

  const theme = computed<Theme>(() => (preference.value === "system" ? (systemDark.value ? "dark" : "light") : preference.value))
  const isDark = computed(() => theme.value === "dark")

  function setPreference(next: ThemePreference): void {
    preference.value = next
    try {
      if (next === "system") localStorage.removeItem(THEME_STORAGE_KEY)
      else localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch {}
  }

  watch(theme, value => (document.documentElement.dataset.theme = value), { immediate: true })

  return { preference, theme, isDark, setPreference }
})
